import React, { useState } from 'react'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import FormControl from '@mui/material/FormControl'
import Grid from '@mui/material/Grid'
import IconButton from '@mui/material/IconButton'
import InputLabel from '@mui/material/InputLabel'
import MenuItem from '@mui/material/MenuItem'
import Paper from '@mui/material/Paper'
import Select from '@mui/material/Select'
import Typography from '@mui/material/Typography'
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward'
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward'
import { Link, router } from '@inertiajs/react'
import { SidebarLayout } from '@/components/layouts/SidebarLayout'
import { ConfirmDialog } from '@/components/shared/ConfirmDialog'
import { useSortable } from '@/hooks/useSortable'

interface Tool {
  id: number
  name: string
  slug: string
  description: string | null
  tool_type: string
  agent_count: number
  created_by: string
  created_at: string
}

interface Props {
  tools: Tool[]
}

const TOOL_TYPE_LABELS: Record<string, string> = {
  web_service: 'Web Service',
  mcp: 'MCP',
}

const SORT_OPTIONS = [
  { value: 'name', label: 'Name' },
  { value: 'tool_type', label: 'Type' },
  { value: 'agent_count', label: 'Agents' },
  { value: 'created_by', label: 'Created by' },
  { value: 'created_at', label: 'Created' },
]

export default function Index({ tools }: Props) {
  const { sorted, order, orderBy, handleSort } = useSortable(tools, 'name')
  const [deleteTarget, setDeleteTarget] = useState<Tool | null>(null)

  function handleDelete() {
    if (deleteTarget) {
      router.delete(`/manage/tools/${deleteTarget.id}`)
      setDeleteTarget(null)
    }
  }

  return (
    <SidebarLayout
      section="manage"
      title="Tools"
      action={
        <Button component={Link} href="/manage/tools/new" variant="contained">
          New tool
        </Button>
      }
    >
      {tools.length > 1 && (
        <Box sx={{ display: 'flex', gap: 1, mb: 2, alignItems: 'center' }}>
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Sort by</InputLabel>
            <Select
              value={orderBy}
              label="Sort by"
              onChange={e => handleSort(e.target.value)}
            >
              {SORT_OPTIONS.map(opt => (
                <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <IconButton size="small" onClick={() => handleSort(orderBy)}>
            {order === 'asc' ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />}
          </IconButton>
        </Box>
      )}

      {sorted.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 8 }}>
          No tools found. Create one to get started.
        </Typography>
      ) : (
        <Grid container spacing={2}>
          {sorted.map((tool) => (
            <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={tool.id}>
              <Paper
                elevation={0}
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                  p: 2,
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
                  <Typography variant="subtitle2" fontWeight={600} sx={{ fontFamily: 'monospace', flexGrow: 1, mr: 1 }}>
                    {tool.name}
                  </Typography>
                  <Chip
                    label={TOOL_TYPE_LABELS[tool.tool_type] || tool.tool_type}
                    size="small"
                    variant="outlined"
                    color={tool.tool_type === 'mcp' ? 'secondary' : 'default'}
                  />
                </Box>

                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {tool.description || '—'}
                </Typography>

                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 1.5 }}>
                  <Chip label={`${tool.agent_count} agents`} size="small" variant="outlined" />
                </Box>

                <Typography variant="caption" color="text.secondary" sx={{ mb: 1.5 }}>
                  By {tool.created_by} · {new Date(tool.created_at).toLocaleDateString()}
                </Typography>

                <Box sx={{ display: 'flex', gap: 1, mt: 'auto' }}>
                  <Button component={Link} href={`/manage/tools/${tool.id}/edit`} size="small">
                    Edit
                  </Button>
                  <Button size="small" color="error" onClick={() => setDeleteTarget(tool)}>
                    Delete
                  </Button>
                </Box>
              </Paper>
            </Grid>
          ))}
        </Grid>
      )}

      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete tool"
        message={`Are you sure you want to delete tool '${deleteTarget?.name}'? This cannot be undone.`}
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </SidebarLayout>
  )
}
